import React, { useState } from "react";


import { useLiveUpdates } from "@hooks/useLiveUpdates";
import { SectionDuplex } from "@sections/SectionDuplex";
import { IdTooltip } from "@src/components/elements/IdTooltip";
import { buildThemeClasses } from "@src/utils/theme";
import { getContentfulPreviewPropsFn } from "@utils";
import clsx from "clsx";
import { graphql } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

export function SectionTabs({ entry: rawEntry }) {
  const entry = useLiveUpdates(rawEntry);

  const getPreviewProps = getContentfulPreviewPropsFn(entry.contentful_id);
  const {overline} = entry;
  const {headline} = entry;
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const tabs = useLiveUpdates(entry.tabs);
  const activeTab = tabs.at?.(activeItemIndex);

  return (
    <section
      className={clsx(
        'py-section font-atkinson',
        {...buildThemeClasses(entry.customTheme)}
      )}
      id={entry.contentful_id}
    >
      <IdTooltip id={entry.contentful_id} />
      <div className="inner-small" {...getPreviewProps("internalName")}>
        {overline && (
          <p
            className="uppercase text-emphasis"
            {...getPreviewProps("overline")}
          >
            {overline}
          </p>
        )}
        {headline && (
          <h2 className="mt-2 mb-6 text-rt-l plain" {...getPreviewProps("headline")}>
            {headline}
          </h2>
        )}
        {tabs.at?.(0) && (
          <div className="flex md:grid md:grid-cols-4 w-full overflow-x-scroll scrollbar--transparent gap-4 mb-7 md:mb-16">
            {tabs.map((tab, index) => {
              const isActiveTab = activeItemIndex === index;
              const activeTabClasses = clsx(
                  "p-11px border-2 border-accent",
              );
              return (
              // eslint-disable-next-line react/button-has-type
              <button
                key={tab.contentful_id}
                className={clsx(
                  "flex text-left items-center gap-3 rounded-lg shrink-0 w-200px md:w-auto",
                  isActiveTab && activeTabClasses,
                  !isActiveTab && 'p-3 border border-base',
                )}
                onClick={() => {
                  setActiveItemIndex(index);
                }}
              >
                {tab.icon?.file?.url && (
                  <div
                    className="w-32px h-32px rounded flex items-center justify-center"
                    {...getContentfulPreviewPropsFn(tab.contentful_id)("icon")}
                  >
                    {tab.icon?.small && (
                      <GatsbyImage
                        alt=""
                        aria-hidden
                        className="w-8 h-8"
                        image={getImage(tab.icon?.small)}
                      />
                    )}
                    {!tab.icon?.small && (
                      <img
                        alt=""
                        aria-hidden
                        height="32"
                        src={tab.icon.file.url}
                        width="32"
                      />
                    )}
                  </div>
                )}
                <span
                  {...getContentfulPreviewPropsFn(tab.contentful_id)(
                    "compactHeading"
                  )}
                >
                  {tab.compactHeading}
                </span>
              </button>
            )})}
          </div>
        )}
        <SectionDuplex
          key={`duplex-tab-${activeItemIndex}`}
          entry={{ ...activeTab, customTheme: entry.customTheme }}
          isInsideTabs
        />
      </div>
    </section>
  );
}

export const SectionTabsFragment = graphql`
  fragment SectionTabs on ContentfulSectionTabs {
    contentful_id
    __typename
    sys {
      id
    }
    headline
    overline
    customTheme
    tabs {
      ...SectionDuplex
    }
  }
`;
